import { useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import * as api from "../api/client";

// Create/edit for one role. The name is fixed once created — it's what users.role stores, so renaming
// it here would silently orphan every user already assigned to it.
//
// A grant is a table plus, optionally, the specific columns of it the role may read. Expanding a table
// shows its real columns with their admin-written descriptions, each selectable. Columns are fetched
// lazily per table and cached, since most roles only ever narrow one or two.
//
// "All columns selected" and "table not narrowed at all" are deliberately the same thing, collapsed on
// submit: a role that happens to tick every box is a whole-table grant, and storing it as an explicit
// column list would freeze today's columns into it — the next dataset load adds a column and the role
// would silently keep excluding it.
export default function RoleFormModal({ mode, initial, tables, docFolders = [], saving, error, onSubmit, onClose }) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(initial?.name || "");
  const [picked, setPicked] = useState(() => new Set(initial?.tables || []));
  // Knowledge-base folders (ids) the role may read. A ticked category covers all of its sub-categories,
  // including ones created later, so ticking one clears any of its sub-categories ticked on their own.
  const [pickedFolders, setPickedFolders] = useState(() => new Set(initial?.docTags || []));
  const docCategories = docFolders.filter((f) => !f.parentId);
  const subsOf = (id) => docFolders.filter((f) => f.parentId === id);

  function toggleFolder(f) {
    setPickedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(f.id)) next.delete(f.id);
      else {
        next.add(f.id);
        if (!f.parentId) for (const s of subsOf(f.id)) next.delete(s.id);
      }
      return next;
    });
  }
  // { table: Set(columns) } — present only for tables this role narrows. Absent means "whole table",
  // so a table whose columns have never been touched needs no entry and no loaded column list.
  const [colPicks, setColPicks] = useState(() => {
    const m = {};
    for (const [t, cols] of Object.entries(initial?.columns || {})) m[t] = new Set(cols);
    return m;
  });

  const [expanded, setExpanded] = useState(() => new Set());
  const [columnsByTable, setColumnsByTable] = useState({}); // { table: [{name, type, description}] }
  const [loadingTable, setLoadingTable] = useState(null);
  const [columnsError, setColumnsError] = useState({}); // { table: message }

  const allNames = (t) => (columnsByTable[t] || []).map((c) => c.name);
  // Unnarrowed tables read as fully selected, which is what they grant.
  const isColOn = (t, c) => (colPicks[t] ? colPicks[t].has(c) : true);

  function toggle(t) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  function toggleCol(t, c) {
    setColPicks((prev) => {
      // Starting from every column keeps the first click meaning "remove this one", not "keep only
      // this one" — the latter silently drops the rest of the table.
      const cur = prev[t] ? new Set(prev[t]) : new Set(allNames(t));
      if (cur.has(c)) cur.delete(c);
      else cur.add(c);
      return { ...prev, [t]: cur };
    });
  }

  function setAllCols(t, on) {
    setColPicks((prev) => ({ ...prev, [t]: new Set(on ? allNames(t) : []) }));
  }

  /** How many of `t`'s columns this role grants, for the row summary. */
  function grantLabel(t) {
    const sel = colPicks[t];
    if (!sel) return null; // whole table
    const total = allNames(t).length;
    return total ? `${sel.size} of ${total} columns` : `${sel.size} columns`;
  }

  async function toggleExpand(t) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
    if (columnsByTable[t]) return; // already loaded
    setLoadingTable(t);
    try {
      const [cols, descs] = await Promise.all([api.listDbColumns(t), api.listColumnDescriptions(t)]);
      const descMap = {};
      for (const row of descs.descriptions || []) descMap[row.columnName] = row.description;
      setColumnsByTable((prev) => ({
        ...prev,
        [t]: (cols.columns || []).map((c) => ({ ...c, description: descMap[c.name] })),
      }));
    } catch (e) {
      setColumnsError((prev) => ({ ...prev, [t]: e.message }));
    } finally {
      setLoadingTable(null);
    }
  }

  function submit(e) {
    e.preventDefault();
    const columns = {};
    for (const t of picked) {
      const sel = colPicks[t];
      if (!sel) continue; // never narrowed — whole-table grant
      const total = allNames(t).length;
      // `total` is 0 when the table was never expanded, so its columns were never loaded. That happens
      // for a table narrowed in a previous edit and left alone in this one: keep the stored selection
      // rather than mistaking an unloaded list for an empty one.
      if (total && sel.size === total) continue; // everything ticked == whole table
      columns[t] = [...sel];
    }
    onSubmit({ name: name.trim(), tables: [...picked], columns, docTags: [...pickedFolders] });
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-modal" style={{ width: "min(560px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-head">
          <div className="form-modal-title">{isEdit ? `Edit role "${initial.name}"` : "Create role"}</div>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <Form onSubmit={submit}>
          <div className="form-modal-body">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Role name</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isEdit}
                autoFocus={!isEdit}
              />
              {isEdit && <Form.Text className="text-muted">Role name can't be changed after creation.</Form.Text>}
            </Form.Group>
            <Form.Label>Tables this role can query</Form.Label>
            <div className="form-hint">
              Expand a table to grant only some of its columns. Leave every column ticked to grant the
              whole table. A table limited to some columns can't be queried with raw SQL — the
              structured tools still answer over it.
            </div>
            <div className="role-table-list">
              {tables.map((t) => (
                <div key={t} className="role-table-row">
                  <div className="role-table-row-head">
                    <Form.Check
                      type="checkbox"
                      id={`role-modal-${t}`}
                      label={t}
                      checked={picked.has(t)}
                      onChange={() => toggle(t)}
                    />
                    {picked.has(t) && grantLabel(t) && <span className="pill neutral">{grantLabel(t)}</span>}
                    <button type="button" className="link-btn" onClick={() => toggleExpand(t)}>
                      {expanded.has(t) ? "Hide columns" : "Show columns"}
                    </button>
                  </div>
                  {expanded.has(t) && (
                    <div className="role-table-columns">
                      {loadingTable === t ? (
                        <Spinner animation="border" size="sm" />
                      ) : columnsError[t] ? (
                        <div className="col-desc-error">{columnsError[t]}</div>
                      ) : !picked.has(t) ? (
                        // Column choices only mean something once the table itself is granted; showing
                        // live checkboxes here would let someone build a selection that is silently
                        // discarded on save.
                        <div className="col-desc-hint">
                          Grant <strong>{t}</strong> above to choose which of its columns this role can read.
                        </div>
                      ) : (
                        <>
                          <div className="role-column-actions">
                            <button type="button" className="link-btn" onClick={() => setAllCols(t, true)}>
                              Select all
                            </button>
                            <button type="button" className="link-btn" onClick={() => setAllCols(t, false)}>
                              Clear
                            </button>
                          </div>
                          {(columnsByTable[t] || []).map((c) => (
                            <div key={c.name} className="role-table-column-row">
                              <Form.Check
                                type="checkbox"
                                id={`role-col-${t}-${c.name}`}
                                className="col-check"
                                label={c.name}
                                checked={isColOn(t, c.name)}
                                onChange={() => toggleCol(t, c.name)}
                              />
                              <span className="col-desc">
                                {c.description || <em style={{ color: "var(--muted)" }}>no description</em>}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Form.Label className="mt-3">Documents this role can read</Form.Label>
            <div className="form-hint">
              The Knowledge assistant answers this role only from these documents. Tick a category to grant
              everything in it, or tick single sub-categories.
            </div>
            {docCategories.length === 0 ? (
              <div className="col-desc-hint">No document categories yet — create them on the Documents page.</div>
            ) : (
              <div className="role-doc-tree">
                {docCategories.map((c) => {
                  const whole = pickedFolders.has(c.id);
                  const subs = subsOf(c.id);
                  return (
                    <div key={c.id} className="role-doc-cat">
                      <Form.Check
                        type="checkbox"
                        id={`role-docfolder-${c.id}`}
                        label={<strong>{c.name}{subs.length ? " (all)" : ""}</strong>}
                        title={c.description || undefined}
                        checked={whole}
                        onChange={() => toggleFolder(c)}
                      />
                      {subs.map((sub) => (
                        <Form.Check
                          key={sub.id}
                          type="checkbox"
                          className="role-doc-sub"
                          id={`role-docfolder-${sub.id}`}
                          label={sub.name}
                          title={whole ? "Included: the whole category is granted" : sub.description || undefined}
                          checked={whole || pickedFolders.has(sub.id)}
                          disabled={whole}
                          onChange={() => toggleFolder(sub)}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="form-modal-foot">
            <Button variant="outline-secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : isEdit ? "Save changes" : "Create role"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
